/**
 * DigestReturnPill — floating "back-after-Xm" toast shown when a
 * writer returns to the tab/pane after an absence long enough to
 * have likely forgotten where they left off.
 *
 * Spec 23 P9. Visual: dark-ink capsule anchored bottom-left of the
 * task pane, brand-mid outline, warn-colored pulsing dot, mono
 * "BACK AFTER 44M" lead-in, bold "3 things want you" count, a
 * compact "REVIEW" chip, and a × dismiss affordance. Ports
 * ``variant-c-inspector.jsx::DigestReturnPill`` (lines 535–568).
 *
 * SRP: pure presentation. The hook ``useWriterPresence`` emits the
 * ``awayMs`` signal; the shell aggregates "things want you" (needs
 * approval + flagged) and passes both here. Click anywhere opens
 * the review surface; the × dismisses without opening. Both call
 * paths end in ``acknowledge()`` on the host so ``isBack`` clears.
 *
 * Accessibility: ``role="status"`` with ``aria-live="polite"`` on
 * the container — screen readers announce the pill without
 * interrupting. The REVIEW region is a real ``<button>`` with an
 * explicit aria-label; same for the dismiss control.
 *
 * # DECISION: no entrance animation in v1.
 * # WHY: the JSX prototype uses a ``slideUp`` keyframe; porting
 * #      that to Griffel ``makeStyles`` works but adds noise for
 * #      the first iteration. The pill's appearance is already
 * #      directed by the visibilitychange signal (writer just
 * #      returned — they're looking), and the pulsing dot is
 * #      enough motion to draw the eye. Animation can be added
 * #      without API changes when we polish.
 * # DATE: 2026-04-22
 *
 * # DECISION: compose the label from ``formatAwayLabel`` — don't
 * #   accept a pre-formatted string.
 * # WHY: the shell holds ``awayMs`` (number). Pushing the format
 * #      step into the component keeps the grammar consistent with
 * #      future callers (e.g. a digest inspector surface that
 * #      needs the same "44M / 2H 15M" label), and the formatter is
 * #      covered by tests in the hook module.
 * # DATE: 2026-04-22
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { formatAwayLabel } from "../hooks/useWriterPresence";
import { brandColors, fonts, inkColors, statusColors } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DigestReturnPillProps {
  /** Milliseconds the writer was away — rendered via ``formatAwayLabel``. */
  awayMs: number;
  /** How many items want the writer (needs-approval + flagged total). */
  count: number;
  /** Click on the main pill body or REVIEW chip — host should
   *  surface the digest / open the first pending item, then call
   *  ``acknowledge()`` on the hook to clear ``isBack``. */
  onReview: () => void;
  /** × click — host should call ``acknowledge()`` without navigating. */
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Styles — values from variant-c-inspector.jsx's DigestReturnPill (CC_INK
// → inkColors.ink; CC_BRAND → brandColors.primary; CC_BRANDMID →
// brandColors.mid; CC_WARN → statusColors.warn).
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    position: "absolute",
    bottom: "82px",
    left: "12px",
    zIndex: 7,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: inkColors.ink,
    color: "#fff",
    padding: "7px 10px 7px 12px",
    borderRadius: "3px",
    boxShadow: "0 6px 20px -6px rgba(30, 20, 60, 0.4)",
    border: `1px solid ${brandColors.primary}`,
    fontFamily: fonts.body,
    cursor: "pointer",
    // Remove default button styling when the whole pill is a <button>.
    textAlign: "left",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "3px",
    background: statusColors.warn,
    flexShrink: 0,
  },
  lead: {
    fontSize: "9.5px",
    opacity: 0.6,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  count: {
    fontSize: "11.5px",
    fontWeight: 700,
  },
  reviewChip: {
    fontSize: "9.5px",
    padding: "1px 6px",
    borderRadius: "2px",
    background: brandColors.mid,
    color: "#fff",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
  },
  dismiss: {
    fontSize: "12px",
    opacity: 0.5,
    marginLeft: "2px",
    padding: "0 4px",
    background: "transparent",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    lineHeight: 1,
    "&:hover": {
      opacity: 1,
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Plural-aware "things want you" copy. Exported for tests — the
 * grammar is the part worth locking down.
 */
export function formatThingsLabel(count: number): string {
  // "1 thing wants you" reads more naturally than "1 things want you"
  // and matches how a human would phrase it. Above 1 stays plural.
  if (count === 1) return "1 thing wants you";
  return `${count} things want you`;
}

export const DigestReturnPill: React.FC<DigestReturnPillProps> = ({ awayMs, count, onReview, onDismiss }) => {
  const styles = useStyles();
  const awayLabel = formatAwayLabel(awayMs);
  const thingsLabel = formatThingsLabel(count);

  // Clicking the pill body reviews; the × stops propagation so it
  // dismisses without navigating.
  const handleDismiss = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDismiss();
  };

  return (
    <output className={styles.root} aria-live="polite" aria-label={`You were away ${awayLabel}. ${thingsLabel}.`}>
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.lead}>back after {awayLabel}</span>
      <span className={styles.count}>{thingsLabel}</span>
      <button
        type="button"
        className={styles.reviewChip}
        onClick={(e) => {
          e.stopPropagation();
          onReview();
        }}
        aria-label="Review pending items"
      >
        Review
      </button>
      <button type="button" className={styles.dismiss} onClick={handleDismiss} aria-label="Dismiss back-after notice">
        ×
      </button>
    </output>
  );
};

export default DigestReturnPill;
